/** The app shell: picks a screen and wires it to the store. */

import { useEffect, useState } from 'react';
import type { BotTier, ReactionId, RoomRules } from '@garbage/shared';
import { useGame } from './store.js';
import { codeFromPath } from './codes.js';
import { refusalText, useT } from './i18n.js';
import { Home } from './components/Home.js';
import { Lobby } from './components/Lobby.js';
import { Table } from './components/Table.js';
import { RoundOver } from './components/RoundOver.js';
import { HowToPlay } from './components/HowToPlay.js';
import { RoundBriefing } from './components/RoundBriefing.js';
import { Dealing } from './components/Dealing.js';
import { SettingsSheet } from './components/SettingsSheet.js';

/**
 * How long the connection may be missing before we say so. The socket is not open yet
 * on first load or a refresh, and flashing the red banner for that moment is noise.
 */
export const OFFLINE_GRACE_MS = 1500;

export function App({
  origin = window.location.origin, onRetry,
}: { origin?: string; onRetry?: () => void } = {}) {
  const store = useGame();
  const t = useT();
  const [helpOpen, setHelpOpen] = useState(false);
  /** Settings, or straight to the feedback form. */
  const [sheet, setSheet] = useState<null | 'settings' | 'feedback'>(null);
  /** The round whose deal we are still showing. Cleared when the animation finishes. */
  const [dealing, setDealing] = useState<number | null>(null);
  const briefingRound = store.screen === 'briefing' ? store.view?.round ?? null : null;

  useEffect(() => {
    // A new round has just been dealt: play the deal before showing the hand.
    if (briefingRound !== null) setDealing(briefingRound);
  }, [briefingRound]);
  const linkCode = typeof window === 'undefined' ? null : codeFromPath(window.location.pathname);

  const [offline, setOffline] = useState(false);
  useEffect(() => {
    if (store.connected) {
      setOffline(false);
      return;
    }
    const handle = setTimeout(() => setOffline(true), OFFLINE_GRACE_MS);
    return () => clearTimeout(handle);
  }, [store.connected]);

  useEffect(() => {
    if (!store.error) return;
    const handle = setTimeout(() => store.dismissError(), 4000);
    return () => clearTimeout(handle);
  }, [store.error]);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = store.locale;
  }, [store.locale]);

  const isHost = store.host !== null && store.host === store.you;
  const openSettings = () => setSheet('settings');
  const react = (reaction: ReactionId) => store.send({ t: 'react', reaction });

  if (helpOpen) {
    return <HowToPlay rules={store.rules} onClose={() => setHelpOpen(false)} />;
  }

  if (dealing !== null && store.view && store.screen === 'briefing') {
    return (
      <Dealing
        players={store.view.players.length}
        cardsEach={store.view.players.find((p) => p.id === store.view!.you)?.handCount ?? 0}
        round={store.view.round}
        pack={store.pack}
        onDone={() => setDealing(null)}
      />
    );
  }

  return (
    <div className="h-full">
      {store.error && (
        <div
          role="alert"
          data-testid="error-toast"
          className="fixed inset-x-0 top-0 z-50 mx-auto mt-[calc(0.5rem+var(--pad-top))] w-fit
                     rounded-xl bg-coral px-4 py-2 text-sm font-bold text-ink shadow-lg"
        >
          {refusalText(t, store.locale, store.error, store.errorCode)}
        </div>
      )}

      {offline && (
        // Not a browser error page: say what is wrong in plain words and offer the
        // one useful action. scope.md §12.
        <div
          data-testid="offline-banner"
          role="status"
          className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-center gap-3
                     bg-coral/95 px-4 py-2 text-sm font-bold text-ink"
        >
          <span>{t('offline.message')}</span>
          {onRetry && (
            <button
              type="button"
              data-testid="retry-button"
              onClick={onRetry}
              className="rounded-lg bg-ink/20 px-3 py-1 text-xs font-black"
            >
              {t('offline.retry')}
            </button>
          )}
        </div>
      )}

      {sheet && (
        <SettingsSheet
          screen={store.screen}
          startWithFeedback={sheet === 'feedback'}
          onClose={() => setSheet(null)}
          onHelp={() => setHelpOpen(true)}
        />
      )}

      {store.screen === 'home' && (
        <Home
          initialCode={linkCode}
          onCreate={(name) => store.send({ t: 'create', name })}
          onJoin={(code, name) => store.send({ t: 'join', code, name })}
          onSettings={openSettings}
        />
      )}

      {store.screen === 'lobby' && store.code && (
        <Lobby
          code={store.code}
          players={store.lobby}
          rules={store.rules}
          isHost={isHost}
          origin={origin}
          onAddBot={(tier: BotTier) => store.send({ t: 'addBot', tier })}
          onRemove={(id) => store.send({ t: 'removeBot', id })}
          onRules={(rules: Partial<RoomRules>) => store.send({ t: 'setRules', rules })}
          onDeal={() => store.send({ t: 'deal' })}
          onHelp={() => setHelpOpen(true)}
          onSettings={openSettings}
        />
      )}

      {store.screen === 'briefing' && store.view && (
        <RoundBriefing
          view={store.view}
          customOrder={store.customOrder}
          pack={store.pack}
          onReorder={store.setCustomOrder}
          onReady={() => store.act({ type: 'READY' })}
          onHelp={() => setHelpOpen(true)}
          onSettings={openSettings}
        />
      )}

      {store.screen === 'table' && store.view && (
        <Table
          view={store.view}
          legal={store.legal}
          claimClosesAt={store.claimClosesAt}
          events={store.lastEvents}
          idleOutAt={store.idleOutAt}
          kickable={store.kickable}
          isHost={isHost}
          onExtendTurn={() => store.send({ t: 'extendTurn' })}
          onKick={(id) => store.send({ t: 'kick', id })}
          onClaimTime={(claimWindowMs) => store.send({ t: 'setClaimTime', claimWindowMs })}
          onReact={react}
          customOrder={store.customOrder}
          pack={store.pack}
          onAct={store.act}
          onReorder={store.setCustomOrder}
          onHelp={() => setHelpOpen(true)}
          onSettings={openSettings}
          onLeave={() => {
            store.send({ t: 'leave' });
            store.reset();
          }}
        />
      )}

      {(store.screen === 'round-over' || store.screen === 'match-over') && store.view && (
        <RoundOver
          view={store.view}
          ready={store.ready}
          isHost={isHost}
          host={store.host}
          onNextRound={() => store.send({ t: 'ready' })}
          onPlayAgain={() => store.send({ t: 'playAgain' })}
          onLeave={() => {
            store.send({ t: 'leave' });
            store.reset();
          }}
          onReact={react}
          onSettings={openSettings}
          onFeedback={() => setSheet('feedback')}
        />
      )}
    </div>
  );
}
